import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Construction, Loader2, Lock, ShieldCheck, ShieldOff, Unlock, XCircle } from 'lucide-react';
import { aktuellerBuildStempel, supabase } from '../../services/supabaseClient';
import { useMaintenance } from '../../hooks/useMaintenance';
import { useMaintenanceCountdown } from '../../hooks/useMaintenanceCountdown';
import { useProjectLocks } from '../../hooks/useProjectLocks';
import { TorNotschalterKarte } from './TorNotschalterKarte';
import { endMaintenance, fetchDeployedBuildTime, setMinBuild, startMaintenance } from '../../services/maintenanceService';
import {
    deployedBuildForStart,
    formatMaintenanceCountdown,
    MAINTENANCE_DEFAULT_DURATION_MINUTES,
    MAINTENANCE_DEFAULT_MINUTES,
    MAINTENANCE_MAX_DURATION_MINUTES,
    MAINTENANCE_MIN_DURATION_MINUTES,
    formatBuildStempel,
    istUnterMindestBuild,
} from '../../services/maintenanceLogic';
import {
    isLockStale,
    lockHolderName,
    ProjectLock,
} from '../../services/projectLockLogic';

/**
 * Die Admin-Karte der Bauphase: ankuendigen, zusehen, beenden.
 *
 * Die Regeln (Phase, Countdown, Ablauf) stehen in maintenanceLogic.ts, das
 * Reden mit der Datenbank in maintenanceService.ts, die Zustandsfuehrung im
 * Hook useMaintenance. Hier steht nur, was auf dem Schirm erscheint — und
 * die eine Zusatzinformation, die der Admin waehrend der Sperre braucht: WER
 * noch in einem Projekt sitzt. Erst bei „0 offene Sperren" darf deployt
 * werden; ein Deploy, waehrend jemand noch schreibt, war am 07.09.2026 der
 * Weg, auf dem ein alter Build stundenlang frische Zaehler ueberschrieb.
 *
 * ─── Woher die Sperrliste kommt ────────────────────────────────────────────
 * Zwei Wege, je nachdem, was der Aufrufer weiss:
 *   * `projectIds` gefuellt → useProjectLocks (Realtime + Herzschlag-Abgleich),
 *     dieselbe Quelle wie der Sperrhinweis in der Projektliste.
 *   * `projectIds` leer (AdminGlobalSettings kennt keine Projektliste) → die
 *     Karte liest die GANZE Tabelle `project_locks`. Das darf sie: die
 *     Select-Policy `project_locks_select_project_or_admin` laesst Admins
 *     jede Zeile sehen. Ohne `in`-Filter faellt zugleich die 300er-Deckelung
 *     der Liste weg — genau richtig, denn hier zaehlt jede einzelne Sperre.
 * Projektnummer und -name werden fuer die gefundenen Sperren nachgeladen,
 * damit der Admin nicht vor einer Liste aus UUIDs sitzt.
 *
 * ─── Sekundentakt ──────────────────────────────────────────────────────────
 * Der Hook rechnet in 'announced' nur alle 30 s nach (bewusst, siehe
 * describeMaintenance). Die Karte will aber immer die Sekunde zeigen — der
 * Admin sitzt davor und wartet. Den eigenen 1-s-Zeitgeber, der die zuletzt
 * vom Hook gelieferte (server-korrigierte) Restzeit fortschreibt, liefert
 * useMaintenanceCountdown — derselbe wie in der Leiste oben.
 */

interface MaintenancePanelProps {
    currentUser: { id?: string; role?: string } | null;
    /** Alle Projekte, die der Admin sieht; leer = Sperren aus der ganzen Tabelle. */
    projectIds: string[];
}

/** Wie oft die Tabellen-Variante nachsieht — der Herzschlag ist 45 s, dichter bringt nichts. */
const FALLBACK_LOCK_POLL_MS = 15_000;

const MAX_MESSAGE_LENGTH = 200;
const MIN_MINUTES = 1;
const MAX_MINUTES = 120;

const EMPTY_LOCKS: Map<string, ProjectLock> = new Map();

interface ProjectLabel {
    number: string | null;
    name: string | null;
}

/** „vor 12 s" / „vor 3 min" — Alter des letzten Herzschlags. */
const formatAge = (ms: number): string => {
    const s = Math.max(0, Math.round(ms / 1_000));
    if (s < 60) return `vor ${s} s`;
    return `vor ${Math.floor(s / 60)} min`;
};

/** „45 Minuten" / „1 Stunde" / „1 Stunde 30 Minuten" — fuer die Rueckfrage vor dem Start. */
const formatDuration = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(h === 1 ? '1 Stunde' : `${h} Stunden`);
    if (m > 0 || h === 0) parts.push(m === 1 ? '1 Minute' : `${m} Minuten`);
    return parts.join(' ');
};

/** „HH:MM" in lokaler Zeit. */
const formatClock = (ms: number): string => {
    const d = new Date(ms);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Text eines RPC-Fehlers — PostgREST liefert ein Objekt, ein Wurf ein Error. */
const errorText = (e: unknown): string => {
    if (e instanceof Error) return e.message;
    if (e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string') {
        return (e as { message: string }).message;
    }
    return String(e);
};

const mapLockRow = (row: any): ProjectLock | null => {
    if (!row || !row.project_id || !row.user_id) return null;
    return {
        projectId: row.project_id,
        userId: row.user_id,
        userLabel: row.user_label ?? null,
        acquiredAt: row.acquired_at,
        heartbeatAt: row.heartbeat_at,
    };
};

/**
 * Alle Sperren der Tabelle — nur fuer den Fall ohne Projektliste.
 *
 * FAIL-OPEN wie fetchProjectLocks: ein Lesefehler ergibt eine leere Map und
 * eine Warnung, keinen Wurf. Die Karte zeigt dann „0 offene Sperren" — das
 * ist im Zweifel falsch-gruen, deshalb steht bei einem Fehler zusaetzlich
 * ein Hinweis in der Karte (siehe `lockReadError`).
 */
const fetchAllLocks = async (): Promise<{ locks: Map<string, ProjectLock>; error: string | null }> => {
    const locks = new Map<string, ProjectLock>();
    try {
        const { data, error } = await supabase
            .from('project_locks')
            .select('project_id, user_id, user_label, acquired_at, heartbeat_at');
        if (error) return { locks, error: errorText(error) };
        for (const row of data || []) {
            const lock = mapLockRow(row);
            if (lock) locks.set(lock.projectId, lock);
        }
        return { locks, error: null };
    } catch (e) {
        return { locks, error: errorText(e) };
    }
};

export const MaintenancePanel: React.FC<MaintenancePanelProps> = ({ currentUser, projectIds }) => {
    const role = currentUser?.role ?? null;
    const { state, row, refresh } = useMaintenance({ enabled: true, role });
    const phase = state.phase;
    const isLocked = phase === 'locked';
    const isAnnounced = phase === 'announced' || phase === 'warning';

    // ─── Eingaben fuer die Ankuendigung ───────────────────────────────────
    const [minutes, setMinutes] = useState<number>(MAINTENANCE_DEFAULT_MINUTES);
    const [durationMinutes, setDurationMinutes] = useState<number>(MAINTENANCE_DEFAULT_DURATION_MINUTES);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ─── Sekundentakt: die letzte Hook-Restzeit fortschreiben ─────────────
    // (useMaintenanceCountdown — derselbe Zaehler wie in der Leiste oben.)
    const { remainingMs, nowMs } = useMaintenanceCountdown(state);

    // Serverzeit „jetzt", abgeleitet aus dem Ende und der Restzeit — damit
    // „laeuft seit" nicht gegen die Rechneruhr rechnet. Ohne bekanntes Ende
    // bleibt nur die Rechneruhr.
    const serverNowMs = state.expiresAtMs !== null ? state.expiresAtMs - remainingMs : nowMs;
    const runningMs = state.locksAtMs !== null ? Math.max(0, serverNowMs - state.locksAtMs) : 0;

    // ─── Sperrliste ───────────────────────────────────────────────────────
    const hasIds = projectIds.length > 0;
    const hookLocks = useProjectLocks(projectIds, isLocked && hasIds);

    const [fallbackLocks, setFallbackLocks] = useState<Map<string, ProjectLock>>(EMPTY_LOCKS);
    const [lockReadError, setLockReadError] = useState<string | null>(null);

    useEffect(() => {
        if (!isLocked || hasIds) {
            setFallbackLocks(EMPTY_LOCKS);
            setLockReadError(null);
            return;
        }
        let cancelled = false;
        const load = () => {
            void fetchAllLocks().then((result) => {
                if (cancelled) return;
                setFallbackLocks(result.locks);
                setLockReadError(result.error);
            });
        };
        load();
        const timer = window.setInterval(() => {
            if (typeof document !== 'undefined' && document.hidden) return;
            load();
        }, FALLBACK_LOCK_POLL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [isLocked, hasIds]);

    const locks = hasIds ? hookLocks.locks : fallbackLocks;

    // Projektnummer/-name nachladen — nur fuer Kennungen, die noch fehlen.
    const [labels, setLabels] = useState<Map<string, ProjectLabel>>(new Map());
    const labelsRef = useRef(labels);
    labelsRef.current = labels;

    // `forEach` statt `Array.from(map.values())`: Letzteres liest die
    // tsconfig dieses Projekts als `unknown[]`.
    const lockIdKey = useMemo(() => {
        const ids: string[] = [];
        locks.forEach((_lock, id) => { ids.push(id); });
        return ids.sort().join(',');
    }, [locks]);

    useEffect(() => {
        if (lockIdKey === '') return;
        const missing = lockIdKey.split(',').filter((id) => !labelsRef.current.has(id));
        if (missing.length === 0) return;
        let cancelled = false;
        void supabase
            .from('projects')
            .select('id, name, project_number')
            .in('id', missing)
            .then(({ data, error: readError }) => {
                if (cancelled || readError) return;
                setLabels((prev) => {
                    const next = new Map(prev);
                    for (const row of data || []) {
                        next.set(row.id, {
                            number: typeof row.project_number === 'string' ? row.project_number : null,
                            name: typeof row.name === 'string' ? row.name : null,
                        });
                    }
                    return next;
                });
            });
        return () => { cancelled = true; };
    }, [lockIdKey]);

    const lockRows = useMemo(() => {
        const rows: Array<{ lock: ProjectLock; stale: boolean; ageMs: number; title: string; holder: string }> = [];
        locks.forEach((lock: ProjectLock) => {
            const stale = isLockStale(lock, nowMs);
            const heartbeatMs = Date.parse(lock.heartbeatAt);
            const ageMs = Number.isFinite(heartbeatMs) ? nowMs - heartbeatMs : 0;
            const label = labels.get(lock.projectId);
            const title = [label?.number, label?.name].filter(Boolean).join(' · ');
            rows.push({
                lock,
                stale,
                ageMs,
                title: title !== '' ? title : lock.projectId,
                holder: lockHolderName(lock),
            });
        });
        // Lebende zuerst, dann nach Alter — die frischeste Sperre ganz oben.
        rows.sort((a, b) => Number(a.stale) - Number(b.stale) || a.ageMs - b.ageMs);
        return rows;
    }, [locks, labels, nowMs]);

    const openLockCount = lockRows.filter((r) => !r.stale).length;

    // ─── Aktionen ─────────────────────────────────────────────────────────
    const handleStart = useCallback(async () => {
        const n = Math.round(Number(minutes));
        if (!Number.isFinite(n) || n < MIN_MINUTES || n > MAX_MINUTES) {
            setError(`Vorlauf muss zwischen ${MIN_MINUTES} und ${MAX_MINUTES} Minuten liegen.`);
            return;
        }
        const d = Math.round(Number(durationMinutes));
        if (!Number.isFinite(d) || d < MAINTENANCE_MIN_DURATION_MINUTES || d > MAINTENANCE_MAX_DURATION_MINUTES) {
            setError(`Fenster muss zwischen ${MAINTENANCE_MIN_DURATION_MINUTES} und ${MAINTENANCE_MAX_DURATION_MINUTES} Minuten liegen.`);
            return;
        }
        const ok = window.confirm(
            `Alle Mitarbeiter sehen jetzt einen Countdown von ${n} Minuten. ` +
            'Danach werden ihre Eingaben gesichert, die Projekte geschlossen und die Wartungsseite angezeigt. ' +
            `Die App öffnet spätestens nach ${formatDuration(d)} von selbst wieder – oder früher, sobald die neue Version ausgeliefert ist. Fortfahren?`,
        );
        if (!ok) return;
        setBusy(true);
        setError(null);
        try {
            // Der AUSGELIEFERTE Build kommt frisch aus /version.json; dieser Tab
            // selbst kann seit Tagen offen und damit aelter sein
            // (deployedBuildForStart erklaert, warum das zaehlt).
            const deployedBuild = deployedBuildForStart(aktuellerBuildStempel, await fetchDeployedBuildTime());
            await startMaintenance({
                minutes: n,
                message: message.trim().slice(0, MAX_MESSAGE_LENGTH),
                durationMinutes: d,
                deployedBuild,
            });
            await refresh();
        } catch (e) {
            setError(`Ankündigung fehlgeschlagen: ${errorText(e)}`);
        } finally {
            setBusy(false);
        }
    }, [minutes, durationMinutes, message, refresh]);

    // ─── Build-Riegel: Mindest-Build ──────────────────────────────────────
    //
    // Eigener Zustand, eigene Fehlerzeile: der Riegel gilt UNABHAENGIG von der
    // Bauphase (auch in 'none'), und ein gescheiterter Riegel-Klick darf die
    // Ankuendigungs-Knoepfe nicht sperren.
    const [riegelBusy, setRiegelBusy] = useState(false);
    const [riegelError, setRiegelError] = useState<string | null>(null);
    /** Der gerade AUSGELIEFERTE Build aus /version.json; null = nicht lesbar. */
    const [deployedBuild, setDeployedBuild] = useState<string | null>(null);

    const ladeDeployedBuild = useCallback(async (): Promise<string | null> => {
        const gelesen = await fetchDeployedBuildTime();
        setDeployedBuild(gelesen);
        return gelesen;
    }, []);

    // Einmal beim Oeffnen der Karte. Danach nur noch vor dem Klick (frisch),
    // denn zwischen Oeffnen und Klick kann ein Deploy liegen.
    useEffect(() => { void ladeDeployedBuild(); }, [ladeDeployedBuild]);

    /**
     * Der Riegelstand — UNBEKANNT, solange keine Zeile gelesen ist.
     *
     * `row === null` heisst „nicht gelesen" (offline, Migration nicht
     * gefahren), nicht „nicht gesetzt". Die beiden auseinanderzuhalten ist
     * hier tragend: „Riegel aus" zu behaupten, waehrend in Wahrheit einer
     * steht, waere die gefaehrlichste aller Antworten.
     */
    const riegelLesbar = row !== null;
    const minBuild = row?.minBuild ?? null;
    /** Der eigene Tab liegt unter dem Riegel — dann ist er gleich stillgelegt. */
    const eigenerZuAlt = istUnterMindestBuild(aktuellerBuildStempel, minBuild);
    /**
     * Dieser Tab ist aelter als das, was ausgeliefert ist.
     *
     * Dann darf er den Riegel NICHT setzen: er wuerde sich mit demselben
     * Klick selbst aussperren. Der Server faengt das zwar ab
     * (Aussperr-Schutz der RPC), aber ein Knopf, der nur eine Fehlermeldung
     * erzeugen kann, gehoert nicht bedienbar auf den Schirm.
     */
    const eigenerHinterDeployment = istUnterMindestBuild(aktuellerBuildStempel, deployedBuild);

    const handleSetMinBuild = useCallback(async (wert: string | null, confirmText: string | null) => {
        // `riegelBusy` steht schon vor der Rueckfrage — ein zweiter Klick
        // waehrend des offenen Dialogs startete sonst einen zweiten Lauf.
        setRiegelError(null);
        setRiegelBusy(true);
        try {
            if (confirmText !== null && !window.confirm(confirmText)) return;
            await setMinBuild(wert);
            await refresh();
        } catch (e) {
            // Auch PGRST202 („Nr. 113 nicht gefahren") landet hier und bleibt
            // sichtbar stehen — sonst haelt der Administrator einen Riegel fuer
            // gesetzt, den es gar nicht gibt.
            setRiegelError(`Riegel konnte nicht gesetzt werden: ${errorText(e)}`);
        } finally {
            setRiegelBusy(false);
        }
    }, [refresh]);

    const handleSperren = useCallback(async () => {
        setRiegelError(null);
        setRiegelBusy(true);
        let ausgeliefert: string | null = null;
        try {
            // Frisch lesen: der Riegel darf nur auf einen Stand zeigen, der
            // wirklich ausgeliefert ist — sonst sperrt er die ganze Flotte aus.
            ausgeliefert = await ladeDeployedBuild();
            if (!ausgeliefert) {
                setRiegelError('Der ausgelieferte Build ist gerade nicht lesbar (/version.json). Ohne ihn wird nichts gesperrt.');
                return;
            }
            if (istUnterMindestBuild(aktuellerBuildStempel, ausgeliefert)) {
                setRiegelError(
                    'Dieser Tab läuft mit einem älteren Stand als dem ausgelieferten. '
                    + 'Bitte erst neu laden — sonst sperrt der Riegel als Erstes Sie selbst aus.',
                );
                return;
            }
        } finally {
            setRiegelBusy(false);
        }
        await handleSetMinBuild(
            ausgeliefert,
            [
                `Alte Programmstände werden gesperrt (Stand ${formatBuildStempel(ausgeliefert)}).`,
                'Jeder geöffnete Tab mit einem älteren Stand kann ab sofort nichts mehr speichern, '
                + 'bis er neu geladen wird. Bereits getippte Eingaben bleiben auf dem Gerät und gehen '
                + 'nach dem Neuladen raus — verloren geht nichts.',
                'Fortfahren?',
            ].join('\n\n'),
        );
    }, [handleSetMinBuild, ladeDeployedBuild]);

    const handleEnd = useCallback(async (confirmText: string | null) => {
        if (confirmText !== null && !window.confirm(confirmText)) return;
        setBusy(true);
        setError(null);
        try {
            await endMaintenance();
            await refresh();
        } catch (e) {
            setError(`Beenden fehlgeschlagen: ${errorText(e)}`);
        } finally {
            setBusy(false);
        }
    }, [refresh]);

    // ─── Anzeige ──────────────────────────────────────────────────────────
    return (
        <div>
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                {phase === 'none' && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-[10rem_10rem_1fr] gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Vorlauf (Minuten)</label>
                                <input
                                    type="number"
                                    min={MIN_MINUTES}
                                    max={MAX_MINUTES}
                                    step={1}
                                    value={minutes}
                                    disabled={busy}
                                    onChange={(e) => setMinutes(Number(e.target.value))}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                                />
                                <p className="mt-1 text-[11px] leading-snug text-slate-400">
                                    {MIN_MINUTES}–{MAX_MINUTES}; die letzten zwei Minuten laufen als Sekundenwarnung.
                                </p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fenster (Minuten)</label>
                                <input
                                    type="number"
                                    min={MAINTENANCE_MIN_DURATION_MINUTES}
                                    max={MAINTENANCE_MAX_DURATION_MINUTES}
                                    step={5}
                                    value={durationMinutes}
                                    disabled={busy}
                                    onChange={(e) => setDurationMinutes(Number(e.target.value))}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                                />
                                <p className="mt-1 text-[11px] leading-snug text-slate-400">
                                    {MAINTENANCE_MIN_DURATION_MINUTES}–{MAINTENANCE_MAX_DURATION_MINUTES}; danach öffnet die App von selbst – oder früher, sobald die neue Version da ist.
                                </p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ansage für alle</label>
                                <textarea
                                    rows={2}
                                    maxLength={MAX_MESSAGE_LENGTH}
                                    value={message}
                                    disabled={busy}
                                    placeholder="Neue Version mit …"
                                    onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                                />
                                <p className="mt-1 text-[11px] leading-snug text-slate-400 text-right">
                                    {message.length}/{MAX_MESSAGE_LENGTH}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => { void handleStart(); }}
                            disabled={busy}
                            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Construction className="w-4 h-4" />}
                            Bauphase ankündigen
                        </button>
                    </div>
                )}

                {isAnnounced && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Construction className="w-5 h-5" /></div>
                            <div>
                                <p className="text-sm font-bold text-slate-800">
                                    Angekündigt, Sperre in{' '}
                                    <span className="font-mono tabular-nums">{formatMaintenanceCountdown(remainingMs)}</span>
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {state.message !== '' ? `„${state.message}"` : 'Ohne Ansagetext.'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => { void handleEnd(null); }}
                                disabled={busy}
                                className="bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 border border-slate-300 px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                                Abbrechen
                            </button>
                            <span className="text-[11px] text-slate-400">Nichts wurde geschlossen — der Countdown verschwindet bei allen.</span>
                        </div>
                    </div>
                )}

                {isLocked && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-red-50 text-red-600 rounded-lg"><Lock className="w-5 h-5" /></div>
                            <div>
                                <p className="text-sm font-bold text-slate-800">
                                    Bauphase läuft seit{' '}
                                    <span className="font-mono tabular-nums">{formatMaintenanceCountdown(runningMs)}</span>
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {state.expiresAtMs !== null
                                        ? `Öffnet von selbst um ${formatClock(state.expiresAtMs)} Uhr (noch ${formatMaintenanceCountdown(remainingMs)}) — oder früher, sobald die neue Version ausgeliefert ist.`
                                        : 'Kein Ablaufzeitpunkt bekannt — die Sperre gilt, bis sie hier beendet wird oder die neue Version ausgeliefert ist.'}
                                    {state.message !== '' ? ` Ansage: „${state.message}"` : ''}
                                </p>
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                            <div className={`px-3 py-2 text-xs font-bold ${openLockCount === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                {openLockCount === 0
                                    ? '0 offene Sperren – die Flotte ist draußen'
                                    : `${openLockCount} offene ${openLockCount === 1 ? 'Sperre' : 'Sperren'} – noch nicht deployen`}
                            </div>
                            {lockReadError && (
                                <p className="px-3 py-2 text-xs text-red-600 bg-red-50 border-t border-red-100">
                                    Sperren nicht lesbar: {lockReadError}
                                </p>
                            )}
                            {lockRows.length > 0 && (
                                <ul className="divide-y divide-slate-100">
                                    {lockRows.map((row) => (
                                        <li
                                            key={row.lock.projectId}
                                            className={`px-3 py-2 text-xs flex flex-wrap items-baseline gap-x-3 gap-y-0.5 ${row.stale ? 'text-slate-400' : 'text-slate-700'}`}
                                        >
                                            <span className="font-semibold min-w-0 truncate max-w-full">{row.title}</span>
                                            <span>{row.holder}</span>
                                            <span className="ml-auto font-mono tabular-nums whitespace-nowrap">
                                                {row.stale ? `abgelaufen (${formatAge(row.ageMs)})` : formatAge(row.ageMs)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => { void handleEnd('Alle Clients laden jetzt die neue Version. Deployment ist abgeschlossen?'); }}
                            disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
                            Bauphase beenden
                        </button>
                    </div>
                )}

                {error && (
                    <p className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
                )}

                {/*
                  * ─── Mindest-Build (Build-Riegel, Nr. 112/113) ───────────
                  *
                  * Steht in JEDER Phase: der Riegel ist Normalbetrieb, keine
                  * Bauphase. Er beantwortet die Frage, die nach jedem Deploy
                  * offen bleibt — „was machen die Tabs, die seit gestern offen
                  * sind?". Bis hierher konnte nur der Tab selbst merken, dass
                  * er zu alt ist; der Riegel macht es zur Serversache.
                  */}
                <div className="mt-6 pt-5 border-t border-slate-200 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${riegelLesbar && minBuild ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                            {riegelLesbar && minBuild ? <ShieldCheck className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-800">Mindest-Build</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {!riegelLesbar
                                    ? 'Stand nicht lesbar — die Wartungszeile konnte nicht abgefragt werden. Ob ein Riegel steht, ist von hier aus unbekannt.'
                                    : minBuild
                                        ? `Gesetzt auf ${formatBuildStempel(minBuild)} — ältere Programmstände können nicht mehr speichern.`
                                        : 'Nicht gesetzt — Riegel aus. Auch ein seit Tagen offener Tab darf schreiben.'}
                            </p>
                        </div>
                    </div>

                    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                        <div className="rounded-lg border border-slate-200 px-3 py-2">
                            <dt className="text-[11px] font-bold text-slate-500 uppercase">Riegel steht auf</dt>
                            <dd className="mt-0.5 font-mono tabular-nums text-slate-700">
                                {!riegelLesbar ? 'nicht lesbar' : minBuild ? formatBuildStempel(minBuild) : 'nicht gesetzt'}
                            </dd>
                        </div>
                        <div className="rounded-lg border border-slate-200 px-3 py-2">
                            <dt className="text-[11px] font-bold text-slate-500 uppercase">Dieser Tab</dt>
                            <dd className={`mt-0.5 font-mono tabular-nums ${eigenerZuAlt ? 'text-red-600' : 'text-slate-700'}`}>
                                {formatBuildStempel(aktuellerBuildStempel)}
                            </dd>
                        </div>
                        <div className="rounded-lg border border-slate-200 px-3 py-2">
                            <dt className="text-[11px] font-bold text-slate-500 uppercase">Ausgeliefert</dt>
                            <dd className="mt-0.5 font-mono tabular-nums text-slate-700">
                                {deployedBuild ? formatBuildStempel(deployedBuild) : 'nicht lesbar'}
                            </dd>
                        </div>
                    </dl>

                    {eigenerZuAlt && (
                        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            Dieser Tab liegt selbst unter dem Riegel und kann nicht mehr speichern. Bitte neu laden.
                        </p>
                    )}

                    {eigenerHinterDeployment && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            Erst neu laden: dieser Tab läuft mit einem älteren Stand als dem ausgelieferten.
                            Ein Riegel auf den ausgelieferten Stand würde als Erstes Sie selbst aussperren.
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => { void handleSperren(); }}
                            disabled={riegelBusy || eigenerHinterDeployment}
                            title={eigenerHinterDeployment ? 'Erst neu laden — dieser Tab ist älter als der ausgelieferte Stand.' : undefined}
                            className="bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                        >
                            {riegelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                            Alte Programmstände sperren
                        </button>
                        {/*
                          * IMMER anbieten, nicht nur bei bekanntem `minBuild`:
                          * das Aufheben ist idempotent (die RPC schreibt null),
                          * und gerade wenn der Stand nicht lesbar ist, braucht
                          * der Administrator diesen Notausgang am dringendsten.
                          */}
                        <button
                            type="button"
                            onClick={() => {
                                void handleSetMinBuild(
                                    null,
                                    'Der Riegel wird aufgehoben. Danach darf jeder geöffnete Tab wieder speichern, '
                                    + 'auch einer mit einem Programmstand von vorgestern.\n\nFortfahren?',
                                );
                            }}
                            disabled={riegelBusy}
                            className="bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 border border-slate-300 px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                        >
                            {riegelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                            Riegel aufheben
                        </button>
                        <span className="text-[11px] text-slate-400">
                            Sperrt auf den ausgelieferten Stand. Eingaben bleiben lokal und gehen nach dem Neuladen raus.
                        </span>
                    </div>

                    {riegelError && (
                        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{riegelError}</p>
                    )}
                </div>

                {/*
                  * ─── Tor-Notschalter (S5a Stufe 3b, Nr. 177) ─────────────
                  *
                  * Neben dem Mindest-Build, aus demselben Grund: Normalbetrieb
                  * fuer die ganze Flotte, unabhaengig von der Bauphase.
                  */}
                <TorNotschalterKarte />
            </div>

            <ol className="mt-3 text-[11px] leading-relaxed text-slate-500 list-decimal list-inside space-y-0.5">
                <li>Ankündigen — alle sehen den Countdown und speichern ihre Eingaben.</li>
                <li>Warten, bis hier „0 offene Sperren" steht, dann deployen.</li>
                <li>Neue Version prüfen, dann „Bauphase beenden" — alle Clients laden neu.</li>
                <li>Danach „Alte Programmstände sperren" — Tabs, die den Reload verpasst haben, kommen nicht mehr durch.</li>
            </ol>
        </div>
    );
};
